import type { AccountManualType } from "@bitwarden/web-vault/app/models/types/account.types";
import { Origin } from "@bitwarden/web-vault/app/models/types/general-types";
import type { AccountDetailsTableFilter } from "@bitwarden/web-vault/app/components/tables/account-tables/account-details-table/account-details.table";

/** The label (i18n key) of each manual account type in the filter. */
export const MANUAL_TYPE_LABEL_KEYS: Record<AccountManualType, string> = {
  bank: "accountFilterBank",
  credit: "accountFilterCredit",
  stock: "accountFilterStock",
  other: "accountFilterOther",
};

/** What the filter is doing right now, from the user's point of view. */
export interface AppliedAccountsFilter {
  /** Options the user can actually switch (a zero-count option can't be) that are still ticked. */
  selected: number;
  /** Options the user can actually switch. */
  total: number;
  /** Something the user can control has been unticked, so accounts may be hidden. */
  applied: boolean;
  /** The unticked options, as the filter lists them. Zero-count ones never count: they're locked. */
  hiddenManual: AccountManualType[];
  hiddenSynced: Origin[];
  /** Set when one whole group is unticked and the other group is untouched ("Manual hidden"). */
  wholeGroupHidden: "manual" | "synced" | null;
}

/**
 * Works out which options of the accounts filter are applied. A zero-count type or origin is
 * disabled in the panel, so it is neither a choice the user made nor something they can undo: it
 * must not make the filter look applied (a stuck, unclearable "filtered" state).
 */
export function appliedAccountsFilter(
  filter: AccountDetailsTableFilter | null | undefined,
  manualTypes: readonly AccountManualType[],
  syncedOrigins: readonly Origin[],
  manualCounts: Partial<Record<AccountManualType, number>>,
  syncedCounts: Partial<Record<Origin, number>>,
): AppliedAccountsFilter {
  // A missing count means "not loaded yet", not "none": only a confirmed 0 locks an option.
  const manual = manualTypes.filter((type) => manualCounts[type] !== 0);
  const synced = syncedOrigins.filter((origin) => syncedCounts[origin] !== 0);
  const hiddenManual = manual.filter((type) => !filter?.manual?.[type]);
  const hiddenSynced = synced.filter((origin) => !filter?.synced?.[origin]);
  const total = manual.length + synced.length;
  const selected = filter ? total - hiddenManual.length - hiddenSynced.length : 0;
  const manualAllOff = manual.length > 0 && hiddenManual.length === manual.length;
  const syncedAllOff = synced.length > 0 && hiddenSynced.length === synced.length;

  return {
    selected,
    total,
    applied: total > 0 && selected !== total,
    hiddenManual,
    hiddenSynced,
    wholeGroupHidden:
      manualAllOff && hiddenSynced.length === 0
        ? "manual"
        : syncedAllOff && hiddenManual.length === 0
          ? "synced"
          : null,
  };
}

/** The filter with every option the panel lists ticked again (anything else is left as it was). */
export function resetAccountsFilter(
  filter: AccountDetailsTableFilter,
  manualTypes: readonly AccountManualType[],
  syncedOrigins: readonly Origin[],
): AccountDetailsTableFilter {
  return {
    ...filter,
    manual: {
      ...filter.manual,
      ...Object.fromEntries(manualTypes.map((type) => [type, true])),
    },
    synced: {
      ...filter.synced,
      ...Object.fromEntries(syncedOrigins.map((origin) => [origin, true])),
    },
  };
}

/** "Fiskil", for an origin shown as an option. */
export function syncedOriginName(origin: Origin): string {
  return origin.charAt(0).toUpperCase() + origin.slice(1);
}
